<template>
  <div class="chat-widget-container" :class="{ 'chat-open': isOpen }">
    <!-- Chat Icon Button (Bottom Right) -->
    <div
      v-if="!isOpen"
      class="chat-widget-icon"
      @click="toggleChat"
      :class="{ 'has-notifications': unreadCount > 0 }"
    >
      <i class="material-icons">chat</i>
      <span v-if="unreadCount > 0" class="chat-notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </div>

    <!-- Chat Window -->
    <div v-if="isOpen" class="chat-window">
      <div class="chat-window-header">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">
            <i class="material-icons me-2">chat</i>
            {{ $t('Chat') || 'Chat' }}
          </h6>
          <button class="btn btn-sm btn-link text-white" @click="toggleChat">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <div class="chat-window-body">
        <!-- Conversations List -->
        <div v-if="!selectedUser" class="conversations-list">
          <div class="online-users-section mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="text-muted small mb-0">
                <i class="material-icons" style="font-size: 16px;">people</i>
                {{ $t('Online Users') || 'Online Users' }} ({{ onlineUsers.length }})
              </h6>
              <button 
                class="btn btn-sm btn-link p-0" 
                @click="toggleAllUsers"
                style="font-size: 12px;"
              >
                {{ showAllUsers ? ($t('Show Online Only') || 'Show Online Only') : ($t('Show All Users') || 'Show All Users') }}
              </button>
            </div>
            <div v-if="showAllUsers" class="all-users-list mb-3">
              <ContentSkeleton v-if="loadingAllUsers" variant="list" :rows="3" />
              <div v-else class="all-users">
                <div
                  v-for="user in allUsers"
                  :key="user._id"
                  class="user-item"
                  :class="{ 'is-online': isUserOnline(user._id) }"
                  @click="selectUser(user)"
                >
                  <img
                    :src="user.avatar || defaultAvatar"
                    :alt="user.name"
                    class="user-avatar-small"
                    @error="handleAvatarError"
                  />
                  <span class="user-name">{{ user.name }}</span>
                  <span v-if="isUserOnline(user._id)" class="online-indicator"></span>
                  <span v-else class="offline-indicator" :title="$t('Offline - Message will be delivered when user comes online') || 'Offline - Message will be delivered when user comes online'"></span>
                </div>
              </div>
            </div>
            <div v-else class="online-users-list">
              <div
                v-for="user in onlineUsers"
                :key="user._id"
                class="online-user-item"
                @click="selectUser(user)"
              >
                <img
                  :src="user.avatar || defaultAvatar"
                  :alt="user.name"
                  class="user-avatar-small"
                  @error="handleAvatarError"
                />
                <span class="user-name">{{ user.name }}</span>
                <span class="online-indicator"></span>
              </div>
            </div>
          </div>

          <h6 class="text-muted small mb-2">
            <i class="material-icons" style="font-size: 16px;">history</i>
            {{ $t('Recent Conversations') || 'Recent Conversations' }}
          </h6>
          <ContentSkeleton v-if="loadingConversations" variant="list" :rows="4" />
          <div v-else-if="conversations.length === 0" class="text-center text-muted py-3">
            <i class="material-icons" style="font-size: 48px; opacity: 0.3;">chat_bubble_outline</i>
            <p class="mt-2">{{ $t('No conversations yet') || 'No conversations yet' }}</p>
            <p class="small">{{ $t('Start a conversation by selecting a user above') || 'Start a conversation by selecting a user above' }}</p>
          </div>
          <div v-else class="conversations">
            <div
              v-for="conv in conversations"
              :key="conv.userId"
              class="conversation-item"
              :class="{ 'has-unread': conv.unreadCount > 0 }"
              @click="selectUser(conv)"
            >
              <img
                :src="conv.userAvatar || defaultAvatar"
                :alt="conv.userName"
                class="user-avatar-small"
                @error="handleAvatarError"
              />
              <div class="conversation-info">
                <div class="d-flex justify-content-between">
                  <strong>{{ conv.userName }}</strong>
                  <span v-if="conv.unreadCount > 0" class="badge bg-primary">{{ conv.unreadCount }}</span>
                </div>
                <p class="text-muted small mb-0">
                  {{ formatMessage(conv.lastMessage) }}
                </p>
                <small class="text-muted">{{ formatTime(conv.lastMessage.createdAt) }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Messages -->
        <div v-else class="chat-messages-container">
          <div class="chat-messages-header">
            <div class="d-flex align-items-center">
              <img
                :src="selectedUser.avatar || defaultAvatar"
                :alt="selectedUser.name"
                class="user-avatar-small me-2"
                @error="handleAvatarError"
              />
              <div>
                <strong>{{ selectedUser.name }}</strong>
                <small class="text-muted d-block" v-if="isUserOnline(selectedUser._id)">
                  <span class="online-indicator-small"></span>
                  {{ $t('Online') || 'Online' }}
                </small>
                <small class="text-muted d-block" v-else>
                  <span class="offline-indicator-small"></span>
                  {{ $t('Offline') || 'Offline' }} - {{ $t('Message will be delivered when user comes online') || 'Message will be delivered when user comes online' }}
                </small>
              </div>
            </div>
            <button class="btn btn-sm btn-link" @click="selectedUser = null">
              <i class="material-icons">arrow_back</i>
            </button>
          </div>

          <div class="chat-messages" ref="messagesContainer">
            <ContentSkeleton v-if="loadingMessages" variant="list" :rows="4" padded />
            <div v-else-if="messages.length === 0" class="text-center text-muted py-3">
              <p>{{ $t('No messages yet') || 'No messages yet' }}</p>
            </div>
            <div v-else>
              <div
                v-for="message in messages"
                :key="message._id"
                class="message-item"
                :class="{ 'message-sent': message.sender._id === currentUserId, 'message-received': message.sender._id !== currentUserId }"
              >
                <img
                  v-if="message.sender._id !== currentUserId"
                  :src="message.sender.avatar || defaultAvatar"
                  :alt="message.sender.name"
                  class="message-avatar"
                  @error="handleAvatarError"
                />
                <div class="message-content">
                  <div class="message-bubble">
                    <p v-if="message.message" class="mb-1">{{ message.message }}</p>
                    <div v-if="message.attachments && message.attachments.length > 0" class="attachments-display">
                      <div v-for="(attachment, index) in message.attachments" :key="index" class="attachment-display-item">
                        <a v-if="attachment.url" :href="getAttachmentUrl(attachment.url)" target="_blank" class="attachment-link">
                          <i class="material-icons">attach_file</i>
                          {{ attachment.filename || 'File' }}
                        </a>
                        <span v-else>{{ attachment.filename || 'File' }}</span>
                      </div>
                    </div>
                    <small class="text-muted">{{ formatTime(message.createdAt) }}</small>
                  </div>
                </div>
                <img
                  v-if="message.sender._id === currentUserId"
                  :src="message.sender.avatar || defaultAvatar"
                  :alt="message.sender.name"
                  class="message-avatar"
                  @error="handleAvatarError"
                />
              </div>
            </div>
          </div>

          <div class="chat-input-container">
            <div v-if="selectedAttachments.length > 0" class="attachments-preview mb-2">
              <div v-for="(attachment, index) in selectedAttachments" :key="index" class="attachment-item">
                <span class="attachment-name">{{ attachment.filename }}</span>
                <button class="btn btn-sm btn-link p-0" @click="removeAttachment(index)">
                  <i class="material-icons" style="font-size: 18px;">close</i>
                </button>
              </div>
            </div>
            <div class="input-group">
              <input
                ref="fileInput"
                type="file"
                class="d-none"
                @change="handleFileSelect"
                multiple
                accept="image/*,application/pdf,text/*"
              />
              <button class="btn btn-outline-secondary" @click="triggerFileInput" type="button">
                <i class="material-icons">attach_file</i>
              </button>
              <input
                v-model="newMessage"
                type="text"
                class="form-control"
                :placeholder="$t('Type a message...') || 'Type a message...'"
                @keypress.enter="sendMessage"
              />
              <button class="btn btn-primary" @click="sendMessage" :disabled="!newMessage.trim() && selectedAttachments.length === 0">
                <i class="material-icons">send</i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import chatService from '@/services/chatService';
import websocketService from '@/services/websocketService';
import ContentSkeleton from '@/components/common/ContentSkeleton.vue';

export default {
  name: 'ChatWidget',
  components: { ContentSkeleton },
  data() {
    return {
      isOpen: false,
      conversations: [],
      selectedUser: null,
      messages: [],
      onlineUsers: [],
      allUsers: [],
      unreadCount: 0,
      newMessage: '',
      loadingConversations: false,
      loadingMessages: false,
      loadingAllUsers: false,
      showAllUsers: false,
      currentUserId: null,
      defaultAvatar: process.env.BASE_URL + 'img/default-avatar.png',
      refreshInterval: null,
      onlineUsersInterval: null,
      chatSound: null,
      selectedAttachments: [],
      uploadingAttachments: false
    };
  },
  async mounted() {
    // Get current user ID from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      this.currentUserId = user._id || user.id;
    } else {
      // Fallback to userId from localStorage
      this.currentUserId = localStorage.getItem('userId');
    }

    // Try to register WebSocket listeners (non-blocking)
    // If WebSocket is not available, chat will work in polling mode
    try {
      if (websocketService && typeof websocketService.on === 'function') {
        websocketService.on('chat_message', this.handleChatMessage);
        websocketService.on('chat_message_sent', this.handleChatMessageSent);
        websocketService.on('user_online', this.handleUserOnline);
        websocketService.on('user_offline', this.handleUserOffline);
        websocketService.on('online_users_list', this.handleOnlineUsersList);
        websocketService.on('online_users_list', this.handleOnlineUsersList);
      }
    } catch (error) {
      console.warn('Chat: WebSocket not available, using polling mode', error);
    }

    // Initialize chat sound
    try {
      this.chatSound = new Audio(require('@/modules/chat_module/assets/chat.mp3'));
      this.chatSound.volume = 0.5; // Set volume to 50%
    } catch (error) {
      console.warn('Chat: Could not load sound file', error);
    }

    // Load initial data (don't wait for WebSocket)
    try {
      await Promise.all([
        this.loadConversations(),
        this.loadOnlineUsers(),
        this.loadUnreadCount()
      ]);
    } catch (error) {
      console.error('Chat: Error loading initial data', error);
    }
    
    // Load all users when showing all users list
    if (this.showAllUsers) {
      this.loadAllUsers();
    }

    // No polling needed - WebSocket handles all real-time updates
    // Only use polling as fallback if WebSocket is completely unavailable
    if (!websocketService || !websocketService.isConnected) {
      console.warn('Chat: WebSocket not available, using polling fallback');
      // Only poll if WebSocket is not available
      this.refreshInterval = setInterval(() => {
        if (this.isOpen) {
          this.loadConversations();
          this.loadUnreadCount();
        }
      }, 30000); // Poll every 30 seconds only as fallback

      this.onlineUsersInterval = setInterval(() => {
        this.loadOnlineUsers();
      }, 30000); // Poll online users every 30 seconds only as fallback
    } else {
      console.log('Chat: Using WebSocket for real-time updates (no polling)');
    }
  },
  beforeDestroy() {
    // Safely remove WebSocket listeners
    try {
      if (websocketService && typeof websocketService.off === 'function') {
        websocketService.off('chat_message', this.handleChatMessage);
        websocketService.off('user_online', this.handleUserOnline);
        websocketService.off('user_offline', this.handleUserOffline);
      }
    } catch (error) {
      // Ignore errors when removing listeners
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.onlineUsersInterval) {
      clearInterval(this.onlineUsersInterval);
    }
  },
  watch: {
    selectedUser(newUser) {
      if (newUser) {
        this.loadMessages();
        this.markAsRead();
      }
    },
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    }
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.loadConversations();
        this.loadOnlineUsers();
        this.loadUnreadCount();
      }
    },
    async loadConversations() {
      this.loadingConversations = true;
      try {
        const response = await chatService.getConversations();
        console.log('Chat: Conversations response:', response);
        if (response.success && response.data) {
          // Filter out any invalid conversations
          const validConversations = response.data.filter(conv => 
            conv && conv.userId && conv.userName
          );
          
          // Sort conversations: unread first, then by last message date
          this.conversations = validConversations.sort((a, b) => {
            // First, sort by unread count (higher first)
            if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
            if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
            // If both have unread or both don't, sort by last message date
            const dateA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt) : new Date(0);
            const dateB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt) : new Date(0);
            return dateB - dateA; // Most recent first
          });
          
          console.log('Chat: Loaded conversations:', this.conversations.length);
        } else {
          console.warn('Chat: No conversations data:', response);
          this.conversations = [];
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
        this.conversations = [];
        // Don't block UI on error, just log it
      } finally {
        this.loadingConversations = false;
      }
    },
    async loadMessages() {
      if (!this.selectedUser) return;
      this.loadingMessages = true;
      try {
        const response = await chatService.getMessages(this.selectedUser._id || this.selectedUser.userId);
        if (response.success) {
          // Check if there are new unread messages
          const previousMessageCount = this.messages.length;
          const newMessages = response.data;
          
          // If we had messages before and now we have more, check for new unread ones
          if (previousMessageCount > 0 && newMessages.length > previousMessageCount) {
            const newUnreadMessages = newMessages.slice(previousMessageCount).filter(msg => 
              !msg.read && 
              msg.sender._id !== this.currentUserId && 
              msg.sender._id !== this.currentUserId.toString()
            );
            
            // Play sound if there are new unread messages
            if (newUnreadMessages.length > 0) {
              this.playChatSound();
            }
          }
          
          this.messages = newMessages;
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        this.loadingMessages = false;
      }
    },
    async loadOnlineUsers() {
      try {
        const response = await chatService.getOnlineUsers();
        if (response.success) {
          // Filter out current user (double check in frontend)
          this.onlineUsers = response.data.filter(user => 
            (user._id !== this.currentUserId && user._id !== this.currentUserId.toString()) &&
            (user.id !== this.currentUserId && user.id !== this.currentUserId.toString())
          );
        }
      } catch (error) {
        console.error('Error loading online users:', error);
        // If WebSocket is not available, online users might not work
        // Set empty array to avoid UI issues
        this.onlineUsers = [];
      }
    },
    async loadUnreadCount() {
      try {
        const response = await chatService.getUnreadCount();
        if (response.success) {
          this.unreadCount = response.data.count || 0;
        }
      } catch (error) {
        console.error('Error loading unread count:', error);
        // Don't block UI, just set to 0
        this.unreadCount = 0;
      }
    },
    async loadAllUsers() {
      this.loadingAllUsers = true;
      try {
        const response = await chatService.getAllUsers();
        if (response.ok && response.users) {
          // Filter out current user
          this.allUsers = response.users.filter(user => {
            const userId = user._id || user.id;
            return userId && 
              userId.toString() !== this.currentUserId?.toString() && 
              userId !== this.currentUserId;
          });
        } else if (response.data && Array.isArray(response.data)) {
          // Alternative response format
          this.allUsers = response.data.filter(user => {
            const userId = user._id || user.id;
            return userId && 
              userId.toString() !== this.currentUserId?.toString() && 
              userId !== this.currentUserId;
          });
        }
      } catch (error) {
        console.error('Error loading all users:', error);
        this.allUsers = [];
      } finally {
        this.loadingAllUsers = false;
      }
    },
    toggleAllUsers() {
      this.showAllUsers = !this.showAllUsers;
      if (this.showAllUsers && this.allUsers.length === 0) {
        this.loadAllUsers();
      }
    },
    async sendMessage() {
      if ((!this.newMessage.trim() && this.selectedAttachments.length === 0) || !this.selectedUser) return;

      const messageText = this.newMessage.trim();
      const userId = this.selectedUser._id || this.selectedUser.userId;
      
      // Upload attachments first if any
      let attachments = [];
      if (this.selectedAttachments.length > 0) {
        try {
          this.uploadingAttachments = true;
          attachments = await this.uploadAttachments();
        } catch (error) {
          console.error('Error uploading attachments:', error);
          this.$notify({
            message: this.$tn('Error uploading attachments') || 'Error uploading attachments',
            type: 'danger'
          });
          this.uploadingAttachments = false;
          return;
        } finally {
          this.uploadingAttachments = false;
        }
      }

      // Determine message type
      const messageType = attachments.length > 0 ? (attachments[0].mimeType?.startsWith('image/') ? 'image' : 'file') : 'text';

      // Send via WebSocket (preferred method)
      if (websocketService && websocketService.isConnected) {
        try {
          websocketService.send({
            type: 'chat_message',
            recipient: userId,
            message: messageText || (attachments.length > 0 ? attachments[0].filename : ''),
            messageType: messageType,
            attachments: attachments
          });
          
          // Clear inputs after successful send
          this.newMessage = '';
          this.selectedAttachments = [];
          
          // Optimistically add message to local state
          const tempMessage = {
            _id: 'temp_' + Date.now(),
            sender: {
              _id: this.currentUserId,
              name: 'You',
              avatar: null
            },
            recipient: {
              _id: userId,
              name: this.selectedUser.name,
              avatar: this.selectedUser.avatar
            },
            message: messageText || (attachments.length > 0 ? attachments[0].filename : ''),
            messageType: messageType,
            attachments: attachments,
            createdAt: new Date(),
            read: false,
            isSending: true
          };
          
          this.messages.push(tempMessage);
          this.$nextTick(() => this.scrollToBottom());
          
        } catch (error) {
          console.error('Error sending message via WebSocket:', error);
          // Fallback to HTTP - attachments already uploaded, pass them
          this.sendMessageViaHTTP(userId, messageText, attachments);
        }
      } else {
        // Fallback to HTTP if WebSocket is not available
        this.sendMessageViaHTTP(userId, messageText, attachments);
      }
    },
    async sendMessageViaHTTP(userId, messageText, preUploadedAttachments = []) {
      try {
        // Upload attachments first if any (if not already uploaded)
        let attachments = preUploadedAttachments;
        if (attachments.length === 0 && this.selectedAttachments.length > 0) {
          attachments = await this.uploadAttachments();
        }

        const messageType = attachments.length > 0 ? (attachments[0].mimeType?.startsWith('image/') ? 'image' : 'file') : 'text';
        
        await chatService.sendMessage(userId, messageText || (attachments.length > 0 ? attachments[0].filename : ''), messageType, attachments);
        await this.loadMessages();
        await this.loadConversations();
        
        this.selectedAttachments = [];
      } catch (error) {
        console.error('Error sending message:', error);
        this.$notify({
          message: this.$tn('Error sending message') || 'Error sending message',
          type: 'danger'
        });
      }
    },
    async markAsRead() {
      if (!this.selectedUser) return;
      try {
        const userId = this.selectedUser._id || this.selectedUser.userId;
        await chatService.markAsRead(userId);
        await this.loadUnreadCount();
        await this.loadConversations();
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    },
    selectUser(user) {
      this.selectedUser = {
        _id: user._id || user.userId,
        name: user.name || user.userName,
        avatar: user.avatar || user.userAvatar,
        email: user.email || user.userEmail
      };
    },
    handleChatMessage(message) {
      if (message.data) {
        const msg = message.data;
        const isForCurrentUser = msg.recipient && (
          msg.recipient._id === this.currentUserId || 
          msg.recipient._id === this.currentUserId.toString() ||
          msg.recipient._id?.toString() === this.currentUserId?.toString()
        );
        
        // Play sound if message is for current user and not from current user
        if (isForCurrentUser && msg.sender && 
            msg.sender._id !== this.currentUserId && 
            msg.sender._id?.toString() !== this.currentUserId?.toString()) {
          this.playChatSound();
        }
        
        // If we're viewing this conversation, add message and mark as read
        const selectedUserId = this.selectedUser?._id || this.selectedUser?.userId;
        if (this.selectedUser && (
          (msg.sender && (msg.sender._id === selectedUserId || msg.sender._id?.toString() === selectedUserId?.toString())) ||
          (msg.recipient && (msg.recipient._id === selectedUserId || msg.recipient._id?.toString() === selectedUserId?.toString()))
        )) {
          // Remove any temporary messages with same content
          this.messages = this.messages.filter(m => 
            !(m.isSending && m.message === msg.message && m.recipient?._id === msg.recipient?._id)
          );
          
          // Add message to current conversation
          this.messages.push(msg);
          this.$nextTick(() => {
            this.scrollToBottom();
            this.markAsRead();
          });
        }
        
        // Update unread count locally (increment by 1 if message is for current user)
        // Only update if not viewing the conversation
        if (isForCurrentUser && msg.sender && 
            msg.sender._id !== this.currentUserId && 
            msg.sender._id?.toString() !== this.currentUserId?.toString()) {
          const selectedUserId = this.selectedUser?._id || this.selectedUser?.userId;
          const isViewingConversation = this.selectedUser && (
            (msg.sender && (msg.sender._id === selectedUserId || msg.sender._id?.toString() === selectedUserId?.toString())) ||
            (msg.recipient && (msg.recipient._id === selectedUserId || msg.recipient._id?.toString() === selectedUserId?.toString()))
          );
          
          // Only increment if not viewing this conversation
          if (!isViewingConversation) {
            this.unreadCount = (this.unreadCount || 0) + 1;
          }
        }
        
        // Update conversations list locally (move conversation to top and update last message)
        const conversationIndex = this.conversations.findIndex(conv => 
          (conv.userId === msg.sender._id?.toString() || conv.userId === msg.sender._id) ||
          (conv.userId === msg.recipient._id?.toString() || conv.userId === msg.recipient._id)
        );
        
        if (conversationIndex >= 0) {
          // Update existing conversation
          const conv = this.conversations[conversationIndex];
          conv.lastMessage = msg;
          if (isForCurrentUser && msg.sender._id !== this.currentUserId) {
            conv.unreadCount = (conv.unreadCount || 0) + 1;
          }
          // Move to top
          this.conversations.splice(conversationIndex, 1);
          this.conversations.unshift(conv);
        } else {
          // New conversation - reload to get full user info
          this.loadConversations();
        }
      }
    },
    handleChatMessageSent(message) {
      // Message was successfully sent - remove temporary message and add real one
      if (message.data) {
        const msg = message.data;
        // Remove temporary message
        this.messages = this.messages.filter(m => !m.isSending);
        // Add real message
        this.messages.push(msg);
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        
        // Update conversations list locally (move conversation to top and update last message)
        const conversationIndex = this.conversations.findIndex(conv => 
          (conv.userId === msg.recipient._id?.toString() || conv.userId === msg.recipient._id) ||
          (conv.userId === msg.sender._id?.toString() || conv.userId === msg.sender._id)
        );
        
        if (conversationIndex >= 0) {
          // Update existing conversation
          const conv = this.conversations[conversationIndex];
          conv.lastMessage = msg;
          // Move to top
          this.conversations.splice(conversationIndex, 1);
          this.conversations.unshift(conv);
        } else {
          // New conversation - reload to get full user info
          this.loadConversations();
        }
      }
    },
    playChatSound() {
      if (this.chatSound) {
        try {
          // Reset audio to start
          this.chatSound.currentTime = 0;
          // Play sound
          this.chatSound.play().catch(error => {
            console.warn('Chat: Could not play sound', error);
          });
        } catch (error) {
          console.warn('Chat: Error playing sound', error);
        }
      }
    },
    handleUserOnline(data) {
      // User came online - add to online users list if not already there
      if (data.user && data.user._id !== this.currentUserId) {
        const exists = this.onlineUsers.some(u => 
          (u._id === data.user._id) || (u._id === data.userId)
        );
        if (!exists) {
          this.onlineUsers.push(data.user);
        }
      }
      // No need to reload conversations/unread count - WebSocket handles updates
    },
    handleUserOffline(data) {
      // User went offline - remove from online users list
      if (data.userId) {
        this.onlineUsers = this.onlineUsers.filter(u => 
          (u._id !== data.userId) && (u._id !== data.userId.toString())
        );
      }
    },
    handleOnlineUsersList(data) {
      // Received updated list of online users from WebSocket
      if (data.users && Array.isArray(data.users)) {
        // Filter out current user
        this.onlineUsers = data.users.filter(user => 
          (user._id !== this.currentUserId && user._id !== this.currentUserId.toString()) &&
          (user.id !== this.currentUserId && user.id !== this.currentUserId.toString())
        );
      }
    },
    isUserOnline(userId) {
      return this.onlineUsers.some(u => (u._id === userId || u._id === userId.toString()));
    },
    formatMessage(message) {
      if (!message) return '';
      if (message.messageType === 'file') {
        return '📎 ' + (message.attachments?.[0]?.filename || 'File');
      }
      return message.message || '';
    },
    formatTime(date) {
      if (!date) return '';
      const d = new Date(date);
      const now = new Date();
      const diff = now - d;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return this.$t('Just now') || 'Just now';
      if (minutes < 60) return `${minutes}m ${this.$t('ago') || 'ago'}`;
      if (hours < 24) return `${hours}h ${this.$t('ago') || 'ago'}`;
      if (days < 7) return `${days}d ${this.$t('ago') || 'ago'}`;
      return d.toLocaleDateString();
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },
    handleAvatarError(event) {
      event.target.src = this.defaultAvatar;
    },
    triggerFileInput() {
      this.$refs.fileInput?.click();
    },
    async handleFileSelect(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      // Validate file size (10MB max per file)
      const maxSize = 10 * 1024 * 1024; // 10MB
      for (const file of files) {
        if (file.size > maxSize) {
          this.$notify({
            message: this.$tn('File size must be less than 10MB') || `File ${file.name} is too large. Maximum size is 10MB`,
            type: 'danger'
          });
          return;
        }
      }

      // Add files to selected attachments
      for (const file of files) {
        this.selectedAttachments.push({
          file: file,
          filename: file.name,
          size: file.size,
          mimeType: file.type
        });
      }

      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    removeAttachment(index) {
      this.selectedAttachments.splice(index, 1);
    },
    async uploadAttachments() {
      const { wencesApi } = await import('@/api/wencesApi');
      const uploadedAttachments = [];

      for (const attachment of this.selectedAttachments) {
        try {
          const formData = new FormData();
          formData.append('image', attachment.file);

          const response = await wencesApi.post('/api/upload/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          if (response.data.fileKey) {
            uploadedAttachments.push({
              filename: attachment.filename,
              url: response.data.fileKey,
              size: attachment.size,
              mimeType: attachment.mimeType
            });
          }
        } catch (error) {
          console.error('Error uploading file:', attachment.filename, error);
          throw error;
        }
      }

      return uploadedAttachments;
    },
    async getAttachmentUrl(fileKey) {
      try {
        const { wencesApi } = await import('@/api/wencesApi');
        // Extract clean file key
        let cleanFileKey = fileKey;
        if (fileKey.startsWith('fileuploads/')) {
          cleanFileKey = fileKey.replace('fileuploads/', '');
        }
        const response = await wencesApi.get(`/api/upload/file/${cleanFileKey}`);
        return response.data.url;
      } catch (error) {
        console.error('Error getting attachment URL:', error);
        return '#';
      }
    }
  }
};
</script>

<style scoped>
.chat-widget-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.chat-widget-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.chat-widget-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.chat-widget-icon i {
  color: white;
  font-size: 28px;
}

.chat-widget-icon.has-notifications {
  animation: pulse 2s infinite;
}

.chat-notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  border: 2px solid white;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.chat-window {
  width: 400px;
  height: 600px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Attachments styles */
.attachments-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 8px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: white;
  border-radius: 4px;
  font-size: 12px;
}

.attachment-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachments-display {
  margin-top: 8px;
}

.attachment-display-item {
  margin-bottom: 4px;
}

.attachment-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: inherit;
  text-decoration: none;
}

.attachment-link:hover {
  text-decoration: underline;
}

.attachment-link i {
  font-size: 16px;
}

/* Mobile styles */
@media (max-width: 768px) {
  /* When chat is open, container takes full screen */
  .chat-widget-container.chat-open {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: 10000;
  }

  /* When chat is closed, icon stays in bottom right */
  .chat-widget-container:not(.chat-open) {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
  }

  /* Chat window takes full screen when open */
  .chat-widget-container.chat-open .chat-window {
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}

.chat-window-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px;
}

.chat-window-body {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
}

.conversations-list {
  height: 100%;
}

.online-users-section {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 15px;
}

.online-users-list {
  max-height: 150px;
  overflow-y: auto;
}

.online-user-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
}

.online-user-item:hover {
  background: #f5f5f5;
}

.user-avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 10px;
  object-fit: cover;
}

.user-name {
  flex: 1;
  font-size: 14px;
}

.online-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #28a745;
  margin-left: 8px;
}

.offline-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6c757d;
  margin-left: 8px;
  opacity: 0.5;
}

.online-indicator-small {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #28a745;
  margin-right: 4px;
}

.offline-indicator-small {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6c757d;
  margin-right: 4px;
  opacity: 0.5;
}

.all-users-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
}

.user-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
  margin-bottom: 4px;
}

.user-item:hover {
  background: #f5f5f5;
}

.user-item.is-online {
  background: #e8f5e9;
}

.conversations {
  max-height: 400px;
  overflow-y: auto;
}

.conversation-item {
  display: flex;
  padding: 12px;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: background 0.2s;
}

.conversation-item:hover {
  background: #f5f5f5;
}

.conversation-item.has-unread {
  background: #e3f2fd;
  font-weight: 600;
  border-left: 3px solid #2196f3;
  padding-left: 9px; /* Adjust padding to account for border */
}

.conversation-item.has-unread .conversation-info strong {
  color: var(--bs-primary);
}

.conversation-info {
  flex: 1;
  margin-left: 10px;
}

.chat-messages-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-messages-header {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
}

.message-item {
  display: flex;
  margin-bottom: 15px;
  align-items: flex-end;
}

.message-item.message-sent {
  justify-content: flex-end;
}

.message-item.message-received {
  justify-content: flex-start;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin: 0 8px;
  object-fit: cover;
}

.message-content {
  max-width: 70%;
}

.message-bubble {
  padding: 10px 15px;
  border-radius: 18px;
  background: #f0f0f0;
}

.message-sent .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.message-received .message-bubble {
  background: #e9ecef;
  color: #333;
}

.chat-input-container {
  padding: 15px;
  border-top: 1px solid #e0e0e0;
}

.input-group {
  display: flex;
}

.input-group .form-control {
  border-radius: 20px 0 0 20px;
  border-right: none;
}

.input-group .btn {
  border-radius: 0 20px 20px 0;
}
</style>

